import { useCallback, useState } from "react";

import { Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { PageContainer } from "@/features/ui";

import SearchSvg from "../assets/search.svg";

import { useAuth } from "@/features/authentication";
import { UserOutletsSelect } from "@/features/outlets";
import { apiOutletClient, formatNumber } from "@/features/shared";
import { CenteredLoading, ProductVariantNameCell } from "@/features/ui";
import { DataGrid } from "@mui/x-data-grid";

export const CostOfGoodsSoldReport = () => {
  const { outlet, changeOutlet } = useAuth();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const isExactlyMd = isMdUp && !isLgUp;

  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const columns = [
    {
      field: "name",
      headerName: "Produk Varian",
      flex: 5,
      minWidth: 400,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} linkToStock />
      ),
    },
    {
      field: "totalQuantity",
      headerName: "Terjual",
      type: "number",
      flex: 1,
      minWidth: 100,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "totalSellingPrice",
      headerName: "Total Jual (Rp)",
      type: "number",
      flex: 2,
      minWidth: 150,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "totalCostOfGoodsSold",
      headerName: "Total HPP (Rp)",
      type: "number",
      flex: 2,
      minWidth: 150,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "profit",
      headerName: "Profit (Rp)",
      type: "number",
      flex: 2,
      minWidth: 150,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "margin",
      headerName: "Margin (%)",
      type: "number",
      flex: 1,
      minWidth: 120,
      valueFormatter: (value) => (value !== null ? value : "-"),
    },
  ];

  // Function to fetch data from the API
  const fetchData = useCallback(async () => {
    const [startDate, endDate] = dateRange;
    if (!outlet || !startDate || !endDate) return;

    setData([]);
    setLoading(true);
    setHasSearched(true);
    try {
      const {
        data: { data: responseData },
      } = await apiOutletClient.get("sales-invoices/cost-of-goods-sold", {
        params: {
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
        },
      });
      setData(responseData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, outlet]);

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 5, md: 4, lg: 5 }}>
            <UserOutletsSelect value={outlet} onChange={changeOutlet} />
          </Grid>
          <Grid size={{ xs: 7, md: 6, lg: 5 }}>
            <DateRangePicker
              value={dateRange}
              disableFuture
              slots={{
                field: SingleInputDateRangeField,
              }}
              slotProps={{
                field: {
                  fullWidth: true,
                },
              }}
              format="DD/MMM/YYYY"
              localeText={{ start: "Dari", end: "Sampai" }}
              onChange={(newDateRange) => {
                setDateRange(newDateRange);
              }}
              shouldDisableDate={(date, position) => {
                const [startDate, endDate] = dateRange;
                if (position === "start" && endDate) {
                  return (
                    date.isAfter(endDate) ||
                    date.isBefore(endDate.subtract(6, "day"))
                  );
                }
                if (position === "end" && startDate) {
                  return (
                    date.isBefore(startDate) ||
                    date.isAfter(startDate.add(6, "day"))
                  );
                }
                return false;
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2, lg: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={!isExactlyMd && <SearchIcon />}
              onClick={fetchData}
            >
              {isExactlyMd && <SearchIcon />}
              {!isExactlyMd && "Cari"}
            </Button>
          </Grid>
        </Grid>
        {!loading && !hasSearched && (
          <Stack
            sx={{ justifyContent: "center", alignItems: "center", pt: 10 }}
            spacing={2}
          >
            <Box component="img" src={SearchSvg} sx={{
              height: "20vh"
            }} />
            <Typography sx={{ fontSize: "1.5rem" }}>
              Lakukan pencarian
            </Typography>
          </Stack>
        )}
        {loading && <CenteredLoading sx={{ height: "80vh" }} />}
        {!loading && hasSearched && (
          <DataGrid
            rows={data}
            columns={columns}
            getRowId={(row) => row.sku}
            autoHeight
            disableRowSelectionOnClick
            showToolbar
            initialState={{
              columns: {
                columnVisibilityModel: {
                  sku: false,
                },
              },
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            pageSizeOptions={[25, 50, 100]}
          />
        )}
      </Stack>
    </PageContainer>
  );
};

export default CostOfGoodsSoldReport;
